import type { ChallengeData, GroupData, ProfileData } from "shared/schema";
import { ALLOW_STRING, DATA, STRING, getProps, getWords, simplifyString } from "shelving";
import { useStore } from "shelving/react";
import { Icon, Loader, Page, SubmitButton, TextField, useForm } from "shared/react";
import { CHALLENGE_MAX, GROUPS_MAX, PROFILE_MAX, getKeywordQuery } from "shared/schema";
import { getStringStyle } from "shared/util/style";
import { ChallengeList } from "../challenge/ChallengeList.js";
import { GroupList } from "../group/GroupList.js";
import { ProfileList } from "../profile/ProfileList.js";

const SEARCH_TYPES = {
	profiles: "Profiles",
	groups: "Groups",
	challenges: "Challenges",
};
type SearchType = keyof typeof SEARCH_TYPES;

const SEARCH_SCHEMA = DATA({
	type: ALLOW_STRING<SearchType, string>(SEARCH_TYPES),
	query: STRING,
});

export function SearchPage({ type: initialType }: { type: SearchType }): React.ReactElement {
	const initialQuery = new URL(window.location.href).searchParams.get("q") || "";
	const form = useForm(SEARCH_SCHEMA, { type: initialType, query: initialQuery });
	const { Form, submitted } = form;
	useStore(submitted);
	const { type, query } = !submitted.loading ? submitted.value : form.validated;
	const words = getWords(query).map(simplifyString);
	return (
		<Page //
			title={query ? `"${query}"` : `Search ${type}`}
			path={`/search/${type}${query ? `?q=${query}` : ""}`}
		>
			<header className="panel pad-none" style={getStringStyle("/img/search.angled.svg", type)}>
				<Form>
					<div className="pad-3x width-wide">
						<h1>{`Search ${type}`}</h1>
						<div className="flex-row space-hairline">
							<TextField name="query" placeholder={`Search ${type}...`} />
							<SubmitButton mods="flex-fit">
								<Icon icon="search" title="Search" />
							</SubmitButton>
						</div>
					</div>
					<div className="space-none pad-none flex-row size-small width-wide">
						{getProps(SEARCH_TYPES).map(([k, title]) => (
							<SubmitButton key={k} mods={type === k ? "button-active" : ""} onClick={() => form.publish("type", k)}>
								{title}
							</SubmitButton>
						))}
					</div>
				</Form>
			</header>
			<Loader>
				{!words.length ? null : type === "profiles" ? (
					<ProfileList query={{ ...getKeywordQuery<ProfileData>(words, ["!verified", "!followers"]), $limit: PROFILE_MAX }} />
				) : type === "groups" ? (
					<GroupList query={{ open: true, ...getKeywordQuery<GroupData>(words, ["!verified", "!members"]), $limit: GROUPS_MAX }} />
				) : type === "challenges" ? (
					<ChallengeList query={{ privacy: "public", ...getKeywordQuery<ChallengeData>(words, ["!verified", "!started"]), $limit: CHALLENGE_MAX }} />
				) : (
					type // Never happens.
				)}
			</Loader>
		</Page>
	);
}
